/* ═══════════════════════════════════════════════════════════════════════════
   Toasts
   ───────────────────────────────────────────────────────────────────────────
   Imperative API on purpose. `toast.show(...)` from anywhere, one `<ToastHost>`
   mounted once. A hook-and-context API would force every caller that wants to
   confirm something to be a component inside the right provider, which is the
   friction that makes people skip the confirmation entirely.

   The invisible details are the point here:

   · Transitions, not keyframes. Toasts arrive in bursts, and a keyframe
     restarts from zero when it is interrupted. Transitions re-target from
     wherever the element currently is, which is the only way a second toast
     landing on a first looks intentional.

   · Timers pause when the tab is hidden. Otherwise a toast raised just before
     the user switches away has expired unseen by the time they come back.

   · Timers pause on hover and while dragging, so a toast cannot expire out
     from under the finger that is reaching for its action.

   · Swipe to dismiss commits on velocity as well as distance — a quick flick
     is enough, without dragging the whole way.

   · Enter and exit share a direction, so dismissing feels like pushing the
     toast back out the way it came in.
   ═══════════════════════════════════════════════════════════════════════════ */

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { AnimatePresence, motion, useReducedMotion, type PanInfo } from 'motion/react'
import { AlertTriangle, Check, Info, X } from 'lucide-react'
import { project, springSheet, EASE_OUT } from '../../design/motion'

export type ToastTone = 'neutral' | 'success' | 'error'

export interface ToastSpec {
  id: number
  message: string
  tone: ToastTone
  duration: number
  action?: { label: string; onPress: () => void }
}

type Listener = (toasts: ToastSpec[]) => void

/* A tiny store rather than a context. It has exactly one consumer (the host)
   and needs to be writable from modules that are not components at all. */
class ToastStore {
  private items: ToastSpec[] = []
  private listeners = new Set<Listener>()
  private nextId = 1

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn)
    return () => this.listeners.delete(fn)
  }

  private emit() {
    const snapshot = [...this.items]
    for (const fn of this.listeners) fn(snapshot)
  }

  show(
    message: string,
    opts: { tone?: ToastTone; duration?: number; action?: ToastSpec['action'] } = {},
  ): number {
    const id = this.nextId++
    const spec: ToastSpec = {
      id,
      message,
      tone: opts.tone ?? 'neutral',
      // An action needs time to be read and reached for; a bare confirmation
      // does not.
      duration: opts.duration ?? (opts.action ? 6000 : 3200),
      ...(opts.action ? { action: opts.action } : {}),
    }
    // Three is the point past which the stack stops being informative and
    // starts being a wall. The oldest goes.
    this.items = [...this.items, spec].slice(-3)
    this.emit()
    return id
  }

  success(message: string, opts?: { duration?: number; action?: ToastSpec['action'] }) {
    return this.show(message, { ...opts, tone: 'success' })
  }

  error(message: string, opts?: { duration?: number; action?: ToastSpec['action'] }) {
    return this.show(message, { ...opts, tone: 'error' })
  }

  dismiss(id: number) {
    this.items = this.items.filter((t) => t.id !== id)
    this.emit()
  }
}

export const toast = new ToastStore()

const ICONS: Record<ToastTone, ReactNode> = {
  neutral: <Info size={17} />,
  success: <Check size={17} />,
  error: <AlertTriangle size={17} />,
}

export function ToastHost() {
  const { t } = useTranslation()
  const [items, setItems] = useState<ToastSpec[]>([])
  useEffect(() => toast.subscribe(setItems), [])

  if (typeof document === 'undefined') return null

  return createPortal(
    <section className="toast-host" aria-label={t('a11y.notifications')}>
      <AnimatePresence initial={false}>
        {items.map((spec) => (
          <ToastRow key={spec.id} spec={spec} />
        ))}
      </AnimatePresence>
    </section>,
    document.body,
  )
}

function ToastRow({ spec }: { spec: ToastSpec }) {
  const { t } = useTranslation()
  const reduce = useReducedMotion()
  const [dragging, setDragging] = useState(false)
  const [hovered, setHovered] = useState(false)

  /* The timer is stored as "how much time is left" rather than a fixed
     deadline, so pausing and resuming compose correctly across a hover, a tab
     switch and a drag happening in any order. */
  const remaining = useRef(spec.duration)
  const startedAt = useRef(Date.now())
  const paused = dragging || hovered

  const dismiss = useCallback(() => toast.dismiss(spec.id), [spec.id])

  useEffect(() => {
    if (paused) return

    let timer: number | undefined
    const run = () => {
      startedAt.current = Date.now()
      timer = window.setTimeout(dismiss, remaining.current)
    }
    const hold = () => {
      window.clearTimeout(timer)
      remaining.current = Math.max(0, remaining.current - (Date.now() - startedAt.current))
    }

    const onVisibility = () => (document.hidden ? hold() : run())
    document.addEventListener('visibilitychange', onVisibility)
    if (!document.hidden) run()

    return () => {
      window.clearTimeout(timer)
      document.removeEventListener('visibilitychange', onVisibility)
      remaining.current = Math.max(0, remaining.current - (Date.now() - startedAt.current))
    }
  }, [paused, dismiss])

  const onDragEnd = (_: unknown, info: PanInfo) => {
    setDragging(false)
    // Same physics as the sheet: where the flick lands, not how far it went.
    const projected = info.offset.x + project(info.velocity.x)
    if (Math.abs(projected) > 90) dismiss()
  }

  return (
    <motion.output
      layout={!reduce}
      className={`toast toast--${spec.tone}`}
      initial={reduce ? { opacity: 0 } : { opacity: 0, y: 20, scale: 0.96 }}
      animate={reduce ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1 }}
      exit={reduce ? { opacity: 0 } : { opacity: 0, y: 12, scale: 0.97 }}
      transition={reduce ? { duration: 0.12 } : springSheet}
      drag={reduce ? false : 'x'}
      dragDirectionLock
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={0.9}
      dragMomentum={false}
      onDragStart={() => setDragging(true)}
      onDragEnd={onDragEnd}
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
    >
      <span className="toast-icon" aria-hidden>
        {ICONS[spec.tone]}
      </span>
      <span className="toast-msg">{spec.message}</span>

      {spec.action && (
        <button
          type="button"
          className="toast-action"
          onClick={() => {
            spec.action?.onPress()
            dismiss()
          }}
        >
          {spec.action.label}
        </button>
      )}

      <button type="button" className="toast-x" onClick={dismiss} aria-label={t('a11y.dismiss')}>
        <X size={15} />
      </button>

      {/* A hairline that drains with the timer. It is the only honest way to
          answer "how long do I have to hit Undo", and it is a transform-only
          animation so it costs nothing. */}
      {!reduce && (
        <motion.span
          className="toast-life"
          aria-hidden
          initial={{ scaleX: 1 }}
          animate={{ scaleX: paused ? undefined : 0 }}
          transition={{ duration: remaining.current / 1000, ease: 'linear' }}
        />
      )}
    </motion.output>
  )
}

export { EASE_OUT }
